import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, RefreshCw, Trash2, AlertTriangle } from 'lucide-react';
import { Button, Input, Label, Switch } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import {
  useOrgPluginCatalog,
  useOrgPluginCatalogSources,
  usePutOrgPluginCatalogSources,
  useSyncOrgPluginCatalog,
} from '@/hooks/useOrgPluginCatalog';
import type {
  OrgPluginCatalogEntry,
  OrgPluginCatalogSource,
} from '@/types/plugins';
import {
  PluginGridCard,
  TypeIcon,
} from '../plugins/PluginGridCard';

const SYNC_POLL_INTERVAL_MS = 5_000;
const SYNC_POLL_CEILING_MS = 60_000;

function emptySource(): OrgPluginCatalogSource {
  return { repoUrl: '', ref: '', label: '', enabled: true };
}

function normalizeRepoUrl(url: string): string {
  return url.trim().toLowerCase().replace(/\.git$/, '');
}

/**
 * Strips empty optional fields so the saved config doesn't ship blank strings
 * the backend would treat as set. `enabled` defaults to true if missing.
 */
function sanitizeSource(source: OrgPluginCatalogSource): OrgPluginCatalogSource {
  return {
    repoUrl: source.repoUrl.trim(),
    enabled: source.enabled,
    ...(source.ref?.trim() ? { ref: source.ref.trim() } : {}),
    ...(source.label?.trim() ? { label: source.label.trim() } : {}),
  };
}

export default function OrgPluginCatalogConfig() {
  const { success, error: showError } = useToast();

  const sourcesQuery = useOrgPluginCatalogSources();
  const putSources = usePutOrgPluginCatalogSources();
  const sync = useSyncOrgPluginCatalog();

  const [isSyncing, setIsSyncing] = useState(false);
  const catalogQuery = useOrgPluginCatalog({
    pollIntervalMs: isSyncing ? SYNC_POLL_INTERVAL_MS : undefined,
  });

  const [sources, setSources] = useState<OrgPluginCatalogSource[]>([]);
  const [seeded, setSeeded] = useState(false);

  // Seed local editable state once the server config arrives. Re-seed if the
  // user hasn't started editing yet (no save in flight, no dirty state).
  useEffect(() => {
    if (!seeded && sourcesQuery.data) {
      setSources(sourcesQuery.data.sources);
      setSeeded(true);
    }
  }, [seeded, sourcesQuery.data]);

  // Sync polling: cut off when any catalog entry's lastSyncedAt advances past
  // the pre-sync max, or after a hard ceiling so we don't poll forever if the
  // backend never picks up the job.
  const syncStartRef = useRef<number | null>(null);
  const preSyncMaxRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isSyncing) return;
    if (!catalogQuery.data) return;

    const maxAfter = catalogQuery.data.plugins.reduce<string | null>(
      (acc, p) => (acc && acc > p.lastSyncedAt ? acc : p.lastSyncedAt),
      null,
    );
    const advanced =
      preSyncMaxRef.current === null && (maxAfter !== null || catalogQuery.data.plugins.length === 0)
        ? false
        : maxAfter !== null && maxAfter > (preSyncMaxRef.current ?? '');
    const expired =
      syncStartRef.current !== null &&
      Date.now() - syncStartRef.current >= SYNC_POLL_CEILING_MS;

    if (advanced) {
      setIsSyncing(false);
      syncStartRef.current = null;
      preSyncMaxRef.current = null;
      success('Catalog synced');
    } else if (expired) {
      setIsSyncing(false);
      syncStartRef.current = null;
      preSyncMaxRef.current = null;
      showError('Catalog sync timed out — check source health and try again.');
    }
  }, [isSyncing, catalogQuery.data, success, showError]);

  // Per-source aggregate derived from the catalog entries grouped by repoUrl
  // (case-insensitive, trailing `.git` stripped). Failures bubble up if any
  // entry from that source is in a failed state. We keep the per-source
  // plugin list around so each source card can render a small preview grid.
  const sourceHealth = useMemo(() => {
    type Health = {
      lastSyncedAt: string | null;
      hasFailed: boolean;
      entryCount: number;
      categories: Set<string>;
      authors: Set<string>;
      entries: OrgPluginCatalogEntry[];
    };
    const byRepo = new Map<string, Health>();
    for (const entry of catalogQuery.data?.plugins ?? []) {
      const key = normalizeRepoUrl(entry.repoUrl);
      const prev: Health = byRepo.get(key) ?? {
        lastSyncedAt: null,
        hasFailed: false,
        entryCount: 0,
        categories: new Set<string>(),
        authors: new Set<string>(),
        entries: [],
      };
      prev.entryCount += 1;
      prev.lastSyncedAt =
        prev.lastSyncedAt && prev.lastSyncedAt > entry.lastSyncedAt
          ? prev.lastSyncedAt
          : entry.lastSyncedAt;
      prev.hasFailed = prev.hasFailed || entry.syncStatus === 'failed';
      if (entry.category) prev.categories.add(entry.category);
      const authorName = entry.author?.name?.trim();
      if (authorName) prev.authors.add(authorName);
      prev.entries.push(entry);
      byRepo.set(key, prev);
    }
    return byRepo;
  }, [catalogQuery.data]);

  const handleSave = async () => {
    const sanitized = sources
      .filter((s) => s.repoUrl.trim().length > 0)
      .map(sanitizeSource);
    try {
      await putSources.mutateAsync(sanitized);
      setSources(sanitized);
      success('Catalog sources saved');
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Failed to save sources');
    }
  };

  const handleSync = async () => {
    try {
      preSyncMaxRef.current =
        (catalogQuery.data?.plugins ?? []).reduce<string | null>(
          (acc, p) => (acc && acc > p.lastSyncedAt ? acc : p.lastSyncedAt),
          null,
        ) ?? '';
      syncStartRef.current = Date.now();
      setIsSyncing(true);
      await sync.mutateAsync();
    } catch (e) {
      setIsSyncing(false);
      syncStartRef.current = null;
      preSyncMaxRef.current = null;
      showError(e instanceof Error ? e.message : 'Failed to start sync');
    }
  };

  const addRow = () => setSources((rows) => [...rows, emptySource()]);
  const removeRow = (idx: number) =>
    setSources((rows) => rows.filter((_, i) => i !== idx));
  const updateRow = (idx: number, patch: Partial<OrgPluginCatalogSource>) =>
    setSources((rows) =>
      rows.map((row, i) => (i === idx ? { ...row, ...patch } : row)),
    );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-muted-foreground max-w-2xl">
          Curate marketplace-format git repos here. A background job clones
          each enabled source and caches the discovered plugins so any member
          can one-click install them from the plugin page.
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <Button type="button" variant="outline" onClick={addRow}>
            <Plus className="h-4 w-4 mr-2" />
            Add source
          </Button>
          <Button
            variant="outline"
            onClick={handleSync}
            isLoading={isSyncing}
            disabled={isSyncing || sources.length === 0}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            {isSyncing ? 'Syncing…' : 'Sync now'}
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        <h2 className="text-sm text-muted-foreground">Catalog sources</h2>
        {sources.length === 0 && (
          <div className="rounded-xl border border-dashed border-border bg-card px-6 py-10 text-center text-sm text-muted-foreground">
            No sources yet. Add a marketplace repo URL to start curating
            plugins for your organization.
          </div>
        )}
        <div className="space-y-3">
          {sources.map((source, idx) => {
            const health = sourceHealth.get(normalizeRepoUrl(source.repoUrl));
            // Title hierarchy: the admin's human-friendly label wins, then the
            // marketplace.json `name`, then a generic placeholder so empty rows
            // still announce themselves clearly.
            const title =
              source.label?.trim() || source.metadata?.name || 'New marketplace';
            const ownerName =
              source.metadata?.owner?.name ??
              (health && health.authors.size === 1
                ? [...health.authors][0]
                : undefined);
            const entries = health?.entries ?? [];

            return (
              <div
                key={idx}
                className="rounded-xl border border-border bg-card p-5 space-y-4 transition-colors hover:border-primary-500/40"
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-base font-semibold text-foreground truncate">
                    {title}
                  </h3>
                  <div className="flex items-center gap-2 shrink-0">
                    <Switch
                      checked={source.enabled}
                      onCheckedChange={(checked: boolean) =>
                        updateRow(idx, { enabled: checked })
                      }
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeRow(idx)}
                      aria-label="Remove source"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {source.metadata?.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {source.metadata.description}
                  </p>
                )}

                <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                  <div className="md:col-span-7">
                    <Label htmlFor={`source-url-${idx}`}>Repo URL</Label>
                    <Input
                      id={`source-url-${idx}`}
                      value={source.repoUrl}
                      onChange={(e: { target: { value: string } }) =>
                        updateRow(idx, { repoUrl: e.target.value })
                      }
                      placeholder="https://github.com/org/marketplace.git"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor={`source-ref-${idx}`}>Ref</Label>
                    <Input
                      id={`source-ref-${idx}`}
                      value={source.ref ?? ''}
                      onChange={(e: { target: { value: string } }) =>
                        updateRow(idx, { ref: e.target.value })
                      }
                      placeholder="main"
                    />
                  </div>
                  <div className="md:col-span-3">
                    <Label htmlFor={`source-label-${idx}`}>Label</Label>
                    <Input
                      id={`source-label-${idx}`}
                      value={source.label ?? ''}
                      onChange={(e: { target: { value: string } }) =>
                        updateRow(idx, { label: e.target.value })
                      }
                      placeholder="Internal marketplace"
                    />
                  </div>
                </div>

                {entries.length > 0 && (
                  // 2-row column-flow grid that overflows horizontally — capped
                  // height keeps the source card scannable even when a
                  // marketplace lists dozens of plugins. `-mx-5 px-5` lets the
                  // scrollbar sit flush with the card edges so the snap-points
                  // align with the card's gutters.
                  <div className="-mx-5 px-5 overflow-x-auto">
                    <div className="grid grid-flow-col grid-rows-2 auto-cols-[18rem] gap-3 pb-2">
                      {entries.map((entry) => (
                        <SourcePluginCard
                          key={`${entry.repoUrl}::${entry.subdir}`}
                          entry={entry}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {(health || source.metadata?.version) && (
                  <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-xs text-muted-foreground">
                    {source.metadata?.version && (
                      <span className="font-mono">
                        v{source.metadata.version}
                      </span>
                    )}
                    {health?.lastSyncedAt && (
                      <span>
                        {health.entryCount} plugin
                        {health.entryCount === 1 ? '' : 's'} · Last synced{' '}
                        {new Date(health.lastSyncedAt).toLocaleString()}
                      </span>
                    )}
                    {ownerName && <span>by {ownerName}</span>}
                    {health?.hasFailed && (
                      <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                        <AlertTriangle className="h-3 w-3" />
                        Couldn&rsquo;t refresh some entries
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={handleSave} isLoading={putSources.isPending}>
          Save sources
        </Button>
      </div>
    </div>
  );
}

/**
 * Compact preview card for one plugin inside a catalog source row. No action,
 * no nav — admins are reading what the sync found, not installing from here.
 */
function SourcePluginCard({ entry }: { entry: OrgPluginCatalogEntry }) {
  const metaText = [
    entry.version ? `v${entry.version}` : undefined,
    entry.author?.name?.trim() ? `by ${entry.author.name.trim()}` : undefined,
  ]
    .filter((p): p is string => !!p)
    .join(' · ');

  return (
    <PluginGridCard
      icon={<TypeIcon type={entry.type} size="compact" />}
      title={entry.displayName ?? entry.name}
      description={entry.description?.trim() || entry.name}
      repoUrl={entry.repoUrl}
      subdir={entry.subdir}
      metaText={metaText || undefined}
      size="compact"
    />
  );
}
